// src/pages/admin/UsersList.js
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Button } from "@mui/material";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import { Box } from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, ModalClose, Sheet, Typography } from "@mui/joy";
import Axios from "axios";
import { Link, useNavigate } from "react-router-dom";

export default function UsersList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [callApi, setCallApi] = useState(false);
  const [warning, setWarning] = useState({ text: "", type: "" });
  const [selectedUser, setSelectedUser] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}admin/get-all-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(res.data.Users);
    } catch (e) {
      alert("Session expired");
      localStorage.clear();
      navigate("/login");
    }
  };

  const changeStatus = async (userId) => {
    try {
      await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}admin/change-user-status/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallApi((prev) => !prev);
    } catch (e) {
      alert(e.response?.data?.message || "Failed to change status");
    }
  };

  const deleteUser = async (userId) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}admin/delete-user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallApi((prev) => !prev);
    } catch (e) {
      alert(e.response?.data?.message || "Failed to delete user");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [callApi]);

  const columns = [
    { field: "s_no", headerName: "S.no", width: 80 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "username", headerName: "Username", flex: 1, minWidth: 150 },
    { field: "role", headerName: "Role", flex: 1, minWidth: 120 },
    { field: "phone", headerName: "Phone", flex: 1, minWidth: 120 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "access_level", headerName: "Access Level", flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {/* Edit */}
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => navigate(`/edit-user/${params.row.userId}`)}
          >
            <i className="fas fa-edit" />
          </Button>

          {/* Toggle Active/Inactive */}
          <Button
            size="small"
            variant="contained"
            color={params.row.active ? "success" : "error"}
            onClick={() => {
              setSelectedUser(params.row.userId);
              setWarning({
                text: `Change status to ${params.row.active ? "Inactive" : "Active"}?`,
                type: "status",
              });
              setOpenWarning(true);
            }}
          >
            {params.row.active ? "Active" : "Inactive"}
          </Button>

          {/* Delete */}
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              setSelectedUser(params.row.userId);
              setWarning({ text: "Delete this user?", type: "delete" });
              setOpenWarning(true);
            }}
          >
            <i className="fas fa-trash" />
          </Button>
        </div>
      ),
    },
  ];

  const rows = users?.map((u, idx) => ({
    id: u._id,
    userId: u._id,
    s_no: `${idx + 1}.`,
    name: u.name,
    username: u.username,
    role: u.role === 1 ? "Procurement" : "Foreman",
    phone: u.phone || "—",
    email: u.email,
    access_level: Array.isArray(u.access_level)
      ? u.access_level.join(", ")
      : "",
    active: u.active,
  })) || [];

  return (
    <PageLayout>
      <Grid container spacing={2}>
        <Grid
          size={{ xs: 12 }}
          className="d-flex justify-content-between align-items-center"
        >
          <Breadcrumb title="Users List" />
          <Link to="/add-user">
            <Button variant="contained" color="primary">Create User</Button>
          </Link>
        </Grid>

        <Grid size={{ xs: 12 }} className="mt-3">
          <Box className="mc-card">
            <CardHeader title="Users List" />
            <div style={{ height: 600, width: "100%", marginTop: "1rem" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
                disableRowSelectionOnClick
              />
            </div>
          </Box>
        </Grid>

        {/* Warning User Modal (older aesthetic) */}
        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={openWarning}
          onClose={() => setOpenWarning(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: "1000",
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              width: "30%",
              borderRadius: "md",
              p: 3,
              boxShadow: "lg",
              bgcolor: "#fff",
            }}
          >
            <ModalClose
              variant="outlined"
              sx={{
                top: "calc(-1/4 * var(--IconButton-size))",
                right: "calc(-1/4 * var(--IconButton-size))",
                boxShadow: "0 2px 12px 0 rgba(0 0 0 / 0.2)",
                borderRadius: "50%",
                bgcolor: "#eee",
              }}
            />
            <Typography
              id="basic-modal-dialog-title"
              component="h2"
              level="inherit"
              fontSize="1.25em"
              mb={5}
            >
              {warning.text}
            </Typography>

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                marginTop: "2rem",
              }}
            >
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  if (warning.type === "status") {
                    changeStatus(selectedUser);
                  } else {
                    deleteUser(selectedUser);
                  }
                  setOpenWarning(false);
                }}
                variant="contained"
                color="success"
                style={{
                  width: "8rem",
                  fontSize: "0.8rem",
                }}
              >
                Yes
              </Button>
              <Button
                onClick={() => setOpenWarning(false)}
                variant="contained"
                color="error"
                style={{
                  width: "8rem",
                  fontSize: "0.8rem",
                }}
              >
                No
              </Button>
            </div>
          </Sheet>
        </Modal>
      </Grid>
    </PageLayout>
  );
}
